// View Component: Kullanıcı yorumları bölümü
import React from "react";
import TiltCard from "../UI/TiltCard";
import { TestimonialData } from "../../models/AppData";
import { useIntersectionObserver } from "../../hooks/useLandingPage";

const Testimonials = () => {
  const [sectionRef, isVisible] = useIntersectionObserver({ threshold: 0.2 });

  const sectionStyles = {
    padding: "var(--spacing-9xl) 0 var(--spacing-8xl) 0",
    background: "linear-gradient(135deg, var(--teal-50), var(--blue-50))",
    position: "relative",
  };

  const containerStyles = {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 var(--spacing-lg)",
    position: "relative",
    zIndex: 2,
  };

  const headerStyles = {
    textAlign: "center",
    marginBottom: "var(--spacing-6xl)",
    padding: "var(--spacing-xl) 0",
    background: "rgba(255, 255, 255, 0.4)",
    borderRadius: "var(--radius-xl)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
  };

  const titleStyles = {
    fontFamily: "var(--font-secondary)",
    fontSize: "42px",
    fontWeight: "800",
    color: "var(--gray-900)",
    marginBottom: "var(--spacing-lg)",
    textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    letterSpacing: "-0.5px",
  };

  const subtitleStyles = {
    fontSize: "19px",
    color: "var(--gray-700)",
    maxWidth: "600px",
    margin: "0 auto",
    lineHeight: "1.6",
    fontWeight: "500",
  };

  const gridStyles = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "var(--spacing-3xl)",
    marginTop: "var(--spacing-5xl)",
  };

  const TestimonialCard = ({ testimonial, index }) => {
    const cardStyles = {
      height: "100%",
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      opacity: isVisible ? 1 : 0,
      transition: `all 600ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 200}ms`,
    };

    const headerStyles = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "var(--spacing-2xl)",
    };

    const quoteStyles = {
      fontSize: "17px",
      color: "var(--gray-700)",
      lineHeight: "1.8",
      marginBottom: "var(--spacing-3xl)",
      fontStyle: "normal",
      fontFamily: "var(--font-primary)",
      position: "relative",
      paddingLeft: "var(--spacing-lg)",
    };

    const authorSectionStyles = {
      display: "flex",
      alignItems: "center",
      gap: "var(--spacing-lg)",
      marginTop: "auto",
      padding: "var(--spacing-lg) 0",
      borderTop: "1px solid rgba(148, 163, 184, 0.1)",
    };

    const avatarStyles = {
      width: "64px",
      height: "64px",
      borderRadius: "20px",
      background:
        "linear-gradient(135deg, var(--color-primary), var(--color-accent))",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontSize: "20px",
      fontWeight: "600",
      boxShadow: "0 12px 32px rgba(20, 184, 166, 0.25)",
      position: "relative",
    };

    const authorInfoStyles = {
      flex: 1,
    };

    const nameStyles = {
      fontSize: "18px",
      fontWeight: "700",
      color: "var(--gray-900)",
      marginBottom: "4px",
      fontFamily: "var(--font-secondary)",
    };

    const roleStyles = {
      fontSize: "14px",
      color: "var(--gray-500)",
      marginBottom: "var(--spacing-sm)",
    };

    const starsContainerStyles = {
      display: "flex",
      alignItems: "center",
      gap: "2px",
    };

    const starStyles = {
      fontSize: "16px",
      color: "#F59E0B",
    };

    const quoteIconStyles = {
      width: "48px",
      height: "48px",
      borderRadius: "16px",
      background:
        "linear-gradient(135deg, var(--color-primary)15, var(--color-accent)15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "22px",
      color: "var(--color-primary)",
      fontWeight: "700",
    };

    const decorativePatternStyles = {
      position: "absolute",
      top: "-50px",
      right: "-50px",
      width: "150px",
      height: "150px",
      background:
        "radial-gradient(circle, rgba(20, 184, 166, 0.05), transparent 70%)",
      borderRadius: "50%",
      pointerEvents: "none",
    };

    const verifiedBadgeStyles = {
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      padding: "4px 8px",
      borderRadius: "12px",
      background: "rgba(16, 185, 129, 0.1)",
      color: "#059669",
      fontSize: "12px",
      fontWeight: "600",
    };

    const getInitials = (name) => {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("");
    };

    const renderStars = (rating) => {
      return Array.from({ length: 5 }, (_, i) => (
        <span key={i} style={starStyles}>
          {i < rating ? "★" : "☆"}
        </span>
      ));
    };

    return (
      <TiltCard variant="floating" style={cardStyles} tiltIntensity={6}>
        <div style={decorativePatternStyles} />

        <div style={headerStyles}>
          <div style={quoteIconStyles}>"</div>
          <div style={verifiedBadgeStyles}>✓ Doğrulanmış</div>
        </div>

        <div style={quoteStyles}>
          <div
            style={{
              position: "absolute",
              left: "0",
              top: "0",
              width: "3px",
              height: "100%",
              background:
                "linear-gradient(180deg, var(--color-primary), var(--color-accent))",
              borderRadius: "2px",
            }}
          />
          {testimonial.content}
        </div>

        <div style={authorSectionStyles}>
          <div style={avatarStyles}>
            {getInitials(testimonial.name)}
            <div
              style={{
                position: "absolute",
                inset: "0",
                borderRadius: "20px",
                background:
                  "linear-gradient(45deg, rgba(255,255,255,0.3), transparent)",
                pointerEvents: "none",
              }}
            />
          </div>

          <div style={authorInfoStyles}>
            <div style={nameStyles}>{testimonial.name}</div>
            <div style={roleStyles}>{testimonial.role}</div>
            <div style={starsContainerStyles}>
              {renderStars(testimonial.rating)}
            </div>
          </div>
        </div>
      </TiltCard>
    );
  };

  return (
    <section style={sectionStyles} id="testimonials" ref={sectionRef}>
      {/* Yumuşak geçiş katmanı */}
      <div
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          right: "0",
          height: "60px",
          background:
            "linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />

      <div style={containerStyles}>
        <div style={headerStyles}>
          <h2 style={titleStyles}>What Our Users Say?</h2>
          <p style={subtitleStyles}>
            Discover the experiences of thousands of users about Inbox Zero
          </p>
        </div>

        <div style={gridStyles}>
          {TestimonialData.map((testimonial, index) => (
            <TestimonialCard
              key={testimonial.id}
              testimonial={testimonial}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
